import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { WidgetInsightsSlot } from '../assistant/WidgetInsightCallout';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  useWealthAccountTrends,
  type WealthAccountDelta,
} from '../../hooks/useWealthAccountTrends';
import { useWidgetInsights } from '../../hooks/useWidgetInsights';
import {
  formatObservationDate,
  formatObservationDateLong,
} from '../../utils/balanceObservations';
import { formatCurrency, formatK } from '../charts/d3/format';
import { SpendCategoryChipRail } from '../spend/SpendCategoryChipRail';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';

const CHART_HEIGHT = 132;

function defaultFocusDate(points: { asOfDate: string; balance: number }[]): string | null {
  if (points.length === 0) return null;
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i]!.balance > 0) return points[i]!.asOfDate;
  }
  return points[points.length - 1]?.asOfDate ?? null;
}

function formatDelta(current: number, previous: number): {
  text: string;
  tone: 'up' | 'down' | 'flat';
} {
  const delta = current - previous;
  if (Math.abs(delta) < 0.5) return { text: 'About the same', tone: 'flat' };
  const tone = delta > 0 ? 'up' : 'down';
  const money = `${delta > 0 ? '+' : '−'}${formatCurrency(Math.abs(delta))}`;
  if (previous < 5) return { text: money, tone };
  const pct = Math.round((delta / previous) * 100);
  if (Math.abs(pct) >= 150 || pct === 0) return { text: money, tone };
  return {
    text: `${money} (${pct > 0 ? '+' : ''}${pct}%)`,
    tone,
  };
}

/**
 * Account-type chips + day-level bar chart from portfolio upload snapshots.
 * Tap a date to see balance change vs the previous upload.
 */
export function WealthAccountTrends() {
  const {
    tabs,
    selectedTabId,
    setSelectedTabId,
    points,
    accountDeltas,
    hasHistory,
  } = useWealthAccountTrends({ limit: 8 });

  const [focusedDate, setFocusedDate] = useState<string | null>(null);
  const [userPickedFocus, setUserPickedFocus] = useState(false);

  const activeTab = tabs.find((t) => t.id === selectedTabId);
  const tabLabel = selectedTabId === 'all' ? 'All investments' : (activeTab?.name ?? 'Investments');
  const barColor = activeTab?.color ?? colors.accent;

  const maxAmount = useMemo(
    () => Math.max(...points.map((p) => p.balance), 1),
    [points],
  );

  useEffect(() => {
    if (userPickedFocus && focusedDate && points.some((p) => p.asOfDate === focusedDate)) {
      return;
    }
    setFocusedDate(defaultFocusDate(points));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync focus to latest points
  }, [points]);

  const previousDate = useMemo(() => {
    if (!focusedDate) return null;
    const index = points.findIndex((p) => p.asOfDate === focusedDate);
    if (index <= 0) return null;
    return points[index - 1]?.asOfDate ?? null;
  }, [focusedDate, points]);

  const focusedBalance =
    points.find((p) => p.asOfDate === focusedDate)?.balance ?? 0;
  const previousBalance =
    previousDate != null
      ? (points.find((p) => p.asOfDate === previousDate)?.balance ?? 0)
      : null;

  const deltas = accountDeltas(focusedDate);
  const { insights: aiInsights, loading: aiLoading } = useWidgetInsights('wealth_trends');
  const hasAiInsight = aiInsights.length > 0;
  const showLocalWealthFallback = !aiLoading && !hasAiInsight;

  const handleFocusDate = (asOfDate: string) => {
    setFocusedDate(asOfDate);
    setUserPickedFocus(true);
  };

  const handleClearFocus = () => {
    setFocusedDate(defaultFocusDate(points));
    setUserPickedFocus(false);
  };

  if (!hasHistory) {
    return (
      <GlassCard style={styles.card}>
        <View style={styles.header}>
          <Text style={styles.title}>Trends</Text>
          <RefreshButton compact />
        </View>
        <Text style={styles.hint}>
          Upload a portfolio screenshot — we save that day&apos;s balances to your account. Upload
          again another day to see the change.
        </Text>
        {hasAiInsight ? (
          <WidgetInsightsSlot widget="wealth_trends" />
        ) : null}
      </GlassCard>
    );
  }

  return (
    <GlassCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerText}>
          <Text style={styles.title}>Trends</Text>
          <Text style={styles.hint}>Tap a date to see what changed</Text>
        </View>
        <View style={styles.headerActions}>
          <RefreshButton compact />
          <Text style={styles.badge} numberOfLines={1}>
            {tabLabel}
          </Text>
        </View>
      </View>

      <SpendCategoryChipRail
        categories={tabs}
        selectedId={selectedTabId}
        onSelect={(id) => {
          setSelectedTabId(id);
          setUserPickedFocus(false);
          setFocusedDate(null);
        }}
      />

      {points.length === 0 ? (
        <Text style={styles.empty}>
          No portfolio snapshots yet for {tabLabel}. Import a screenshot to start the timeline.
        </Text>
      ) : (
        <View style={styles.chartBlock}>
          <Text style={styles.axisCaption}>Balance by upload date · {tabLabel}</Text>
          <View style={styles.chartRow}>
            <View style={styles.yAxis}>
              <Text style={styles.yTick}>{formatK(maxAmount)}</Text>
              <Text style={styles.yTick}>{formatK(maxAmount / 2)}</Text>
              <Text style={styles.yTick}>$0</Text>
            </View>
            <View style={styles.chart}>
              {points.map((point) => {
                const amount = point.balance;
                const height = Math.max(
                  (amount / maxAmount) * CHART_HEIGHT,
                  amount > 0 ? 6 : 2,
                );
                const focused = focusedDate === point.asOfDate;
                const isCompare = previousDate === point.asOfDate && !focused;
                const isMuted = focusedDate != null && !focused && !isCompare;

                return (
                  <Pressable
                    key={point.asOfDate}
                    accessibilityRole="button"
                    accessibilityLabel={`${formatObservationDateLong(point.asOfDate)} ${formatCurrency(amount)}`}
                    accessibilityState={{ selected: focused }}
                    onPress={() => handleFocusDate(point.asOfDate)}
                    style={({ pressed }) => [styles.barCol, pressed && styles.pressed]}
                  >
                    <Text style={styles.barAmount} numberOfLines={1}>
                      {amount > 0 ? formatK(amount) : '—'}
                    </Text>
                    <View style={styles.barTrack}>
                      <View style={styles.gridLineTop} />
                      <View style={styles.gridLineMid} />
                      <View
                        style={[
                          styles.barFill,
                          {
                            height,
                            backgroundColor: barColor,
                            opacity: isMuted ? 0.28 : focused ? 1 : isCompare ? 0.55 : 0.72,
                          },
                          focused && styles.barSelected,
                        ]}
                      />
                    </View>
                    <Text style={[styles.barLabel, focused && styles.barLabelSelected]}>
                      {formatObservationDate(point.asOfDate)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          <Text style={styles.xAxisCaption}>Upload date</Text>
        </View>
      )}

      {hasAiInsight ? (
        <WidgetInsightsSlot widget="wealth_trends" style={styles.aiCallout} />
      ) : showLocalWealthFallback && focusedDate ? (
        <InsightPanel
          tabLabel={tabLabel}
          focusedDate={focusedDate}
          previousDate={previousDate}
          focusedBalance={focusedBalance}
          previousBalance={previousBalance}
          deltas={deltas}
          showClear={userPickedFocus}
          onClear={handleClearFocus}
        />
      ) : null}
    </GlassCard>
  );
}

function InsightPanel({
  tabLabel,
  focusedDate,
  previousDate,
  focusedBalance,
  previousBalance,
  deltas,
  showClear,
  onClear,
}: {
  tabLabel: string;
  focusedDate: string;
  previousDate: string | null;
  focusedBalance: number;
  previousBalance: number | null;
  deltas: WealthAccountDelta[];
  showClear: boolean;
  onClear: () => void;
}) {
  const delta =
    previousBalance != null
      ? formatDelta(focusedBalance, previousBalance)
      : { text: 'First snapshot', tone: 'flat' as const };

  return (
    <View style={styles.insight}>
      <View style={styles.insightHeader}>
        <Text style={styles.insightTitle}>
          {formatObservationDateLong(focusedDate)}
          {previousDate ? ` vs ${formatObservationDate(previousDate)}` : ''}
        </Text>
        {showClear ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Reset to latest date"
            hitSlop={8}
            onPress={onClear}
          >
            <Text style={styles.clearText}>Reset</Text>
          </Pressable>
        ) : null}
      </View>

      <Text style={styles.momAmount}>{formatCurrency(focusedBalance)}</Text>
      <Text
        style={[
          styles.deltaText,
          delta.tone === 'up' && styles.deltaUp,
          delta.tone === 'down' && styles.deltaDown,
        ]}
      >
        {tabLabel}: {delta.text}
        {previousDate ? ` vs ${formatObservationDate(previousDate)}` : ''}
      </Text>
      <Text style={styles.compareFoot}>
        {previousDate
          ? 'Change since previous portfolio upload'
          : 'Upload again on another day to track change'}
      </Text>

      {deltas.length > 0 ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Accounts</Text>
          {deltas.map((row) => {
            const rowDelta =
              row.delta != null && row.previousBalance != null
                ? formatDelta(row.currentBalance, row.previousBalance)
                : { text: 'New', tone: 'flat' as const };
            return (
              <View key={row.accountId} style={styles.driverRow}>
                <View style={styles.driverText}>
                  <Text style={styles.driverLabel} numberOfLines={1}>
                    {row.label}
                  </Text>
                  <Text style={styles.driverSub} numberOfLines={1}>
                    {formatCurrency(row.currentBalance)}
                  </Text>
                </View>
                <Text
                  style={[
                    styles.driverDelta,
                    rowDelta.tone === 'up' && styles.deltaUp,
                    rowDelta.tone === 'down' && styles.deltaDown,
                  ]}
                >
                  {rowDelta.text}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  badge: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
    maxWidth: 140,
    textAlign: 'right',
  },
  empty: {
    ...typography.caption,
    color: colors.textMuted,
    paddingVertical: spacing.md,
  },
  chartBlock: {
    gap: spacing.xs,
  },
  axisCaption: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  xAxisCaption: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    textAlign: 'center',
    marginTop: 2,
  },
  chartRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.xs,
  },
  yAxis: {
    width: 40,
    height: CHART_HEIGHT + 36,
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: 18,
    paddingTop: 18,
  },
  yTick: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 10,
    fontVariant: ['tabular-nums'],
  },
  chart: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 6,
    minHeight: CHART_HEIGHT + 52,
    paddingTop: spacing.sm,
  },
  barCol: {
    flex: 1,
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  barAmount: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
    height: 16,
  },
  barTrack: {
    height: CHART_HEIGHT,
    width: '100%',
    maxWidth: 48,
    justifyContent: 'flex-end',
    alignItems: 'center',
    position: 'relative',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  gridLineTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  gridLineMid: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.06)',
  },
  barFill: {
    width: '72%',
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
  },
  barSelected: {
    shadowColor: colors.accent,
    shadowOpacity: 0.35,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 0 },
  },
  barLabel: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    fontWeight: '600',
  },
  barLabelSelected: {
    color: colors.text,
  },
  aiCallout: {
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
  },
  insight: {
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
    gap: spacing.sm,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  insightTitle: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '700',
    flex: 1,
  },
  clearText: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
  },
  momAmount: {
    ...typography.headline,
    color: colors.text,
    fontSize: 22,
    fontVariant: ['tabular-nums'],
  },
  deltaText: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  /** Growth is good for investments. */
  deltaUp: {
    color: colors.accent,
  },
  deltaDown: {
    color: colors.danger,
  },
  compareFoot: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  section: {
    gap: 6,
    marginTop: spacing.xs,
  },
  sectionTitle: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontSize: 11,
  },
  driverRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: 8,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
  },
  driverText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  driverLabel: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  driverSub: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    fontVariant: ['tabular-nums'],
  },
  driverDelta: {
    ...typography.caption,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    color: colors.text,
  },
  pressed: {
    opacity: 0.8,
  },
});
